// src/utils/expandZipStreamed.ts

import { Unzip, AsyncUnzipInflate } from "fflate";
import { expandZipFiles } from "./expandZipFiles";

type StatusType = "uploading" | "done" | "error" | "skipped";

export interface StreamZipCallbacks {
  onStatus?: (file: File, status: StatusType, note?: string) => void;
  /**
   * Called for each extracted file from the ZIP.
   * Implement routing/dup-check/upload in the caller (uploadRouter).
   */
  onEntry: (extracted: File) => Promise<void>;
}

// Above this size we switch from in-memory unzip to streaming unzip.
// Chrome's per-tab single-ArrayBuffer cap is ~2 GiB, so the buffered
// JSZip / fflate-sync paths blow up on multi-GB archives. 500 MB leaves
// plenty of headroom for decompression overhead.
const STREAM_THRESHOLD_BYTES = 500 * 1024 * 1024;

/**
 * Core streaming unzip. Pipes File.stream() into fflate's Unzip class so
 * the browser never holds the whole archive in one ArrayBuffer — peak
 * memory drops to roughly the largest single entry.
 *
 * Each completed entry is emitted via onEntry (called sequentially in
 * the order entries finish decoding).
 */
async function streamZipInternal(
  zipFile: File,
  onEntry: (extracted: File) => void | Promise<void>,
): Promise<number> {
  const reader = zipFile.stream().getReader();
  const entryPromises: Promise<void>[] = [];
  let count = 0;

  return new Promise<number>((resolve, reject) => {
    const unzipper = new Unzip((file) => {
      // Skip directory entries
      if (file.name.endsWith("/")) return;

      // Accumulate decompressed chunks into a Blob. Each chunk is copied
      // (slice) because fflate reuses internal buffers between calls.
      const parts: ArrayBuffer[] = [];

      const entryDone = new Promise<void>((entryResolve, entryReject) => {
        file.ondata = (err, chunk, final) => {
          if (err) return entryReject(err);
          if (chunk && chunk.length > 0) {
            parts.push(chunk.slice().buffer as ArrayBuffer);
          }
          if (final) {
            const baseName = file.name.split("/").pop() || file.name;
            // Blob backs to browser disk-cache when large, keeping the
            // entry off the JS heap.
            const blob = new Blob(parts);
            const out = new File([blob], baseName, { type: "" });

            // Tag with the ZIP-internal directory path (used by Firmex routing)
            const lastSlash = file.name.lastIndexOf("/");
            if (lastSlash > 0) {
              (out as any).__zipDir = file.name.substring(0, lastSlash);
              console.log(`   📦 Tagged "${baseName}" with zipDir="${(out as any).__zipDir}"`);
            }

            count++;
            Promise.resolve(onEntry(out)).then(entryResolve, entryReject);
          }
        };
        file.start();
      });

      entryPromises.push(entryDone);
    });

    // Register deflate decoder so DEFLATE-compressed entries decode.
    unzipper.register(AsyncUnzipInflate);

    (async () => {
      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done) {
            unzipper.push(new Uint8Array(0), true);
            break;
          }
          unzipper.push(value, false);
        }
        // Wait for all in-flight entry decodes to flush before resolving.
        await Promise.all(entryPromises);
        resolve(count);
      } catch (e) {
        reject(e);
      }
    })();
  });
}

/**
 * Stream a ZIP and resolve with the full array of extracted files.
 * Use when the caller wants the complete list before processing.
 */
export async function streamZipToFiles(zipFile: File): Promise<File[]> {
  const out: File[] = [];
  await streamZipInternal(zipFile, (f) => {
    out.push(f);
  });
  return out;
}

/**
 * Threshold-based dispatch. Sub-threshold ZIPs go through the existing
 * JSZip → fflate-sync expander (battle-tested, leave it alone). Large
 * ZIPs go through the streaming path so the whole archive never sits
 * in a single ArrayBuffer.
 */
export async function streamZipEntries({
  zipFile,
  onStatus,
  onEntry,
}: {
  zipFile: File;
} & StreamZipCallbacks) {
  if (zipFile.size <= STREAM_THRESHOLD_BYTES) {
    onStatus?.(zipFile, "uploading", "Reading ZIP with JSZip…");

    try {
      const expandedFiles = await expandZipFiles([zipFile]);

      if (expandedFiles.length === 0) {
        onStatus?.(zipFile, "skipped", "ZIP contains no files or could not be read");
        return;
      }

      for (const extracted of expandedFiles) {
        await onEntry(extracted);
      }

      onStatus?.(
        zipFile,
        "done",
        `Extracted ${expandedFiles.length} file(s) from ZIP`
      );
    } catch (err: any) {
      const reason = err?.message ?? String(err);
      console.error(`❌ streamZipEntries failed via JSZip: ${reason}`, err);
      onStatus?.(zipFile, "error", `ZIP failed: ${reason}`);
    }
    return;
  }

  // Streaming path for large archives
  onStatus?.(
    zipFile,
    "uploading",
    `Streaming ZIP (${(zipFile.size / (1024 * 1024)).toFixed(0)} MB)…`
  );

  try {
    const count = await streamZipInternal(zipFile, async (extracted) => {
      await onEntry(extracted);
    });

    if (count === 0) {
      onStatus?.(zipFile, "skipped", "ZIP contains no files");
      return;
    }

    onStatus?.(zipFile, "done", `Extracted ${count} file(s) from ZIP (streamed)`);
  } catch (err: any) {
    const reason = err?.message ?? String(err);
    console.error(`❌ streamZipEntries failed via streaming: ${reason}`, err);
    onStatus?.(zipFile, "error", `ZIP failed: ${reason}`);
  }
}
